/**
 * write-outbox.ts (v6-S6) — a disk-backed replay queue for agent writes that
 * were attempted while the room server was unreachable.
 *
 * Why: the MCP must never block or break the agent, so an offline write already
 * degrades silently (the client throws, the MCP handler emits an OFFLINE
 * sentinel, the agent keeps going). But that write — a shared intent, a declared
 * contract — was simply LOST. The outbox makes it durable: on a network failure
 * the write is appended here; the next time any write reaches the server, the
 * backlog drains in the exact order it was queued.
 *
 * Only WRITES are queued (reads have no replay value and would return stale
 * data). The queue is bounded: past `maxEntries` the OLDEST entry is dropped and
 * counted, so a long offline stretch can never grow the file without limit.
 *
 * Storage is a single JSONL file (one entry per line). It is append-only in the
 * hot path (cheap, crash-safe-ish); the file is only rewritten on a drain.
 */

import { existsSync, mkdirSync, readFileSync, writeFileSync, appendFileSync } from "node:fs";
import { dirname, join } from "node:path";
import { homedir } from "node:os";
import { createHash } from "node:crypto";

export interface OutboxEntry {
  /** Room-server path the write targeted, e.g. "/hive/intent". */
  path: string;
  /** The JSON body that was to be posted (already includes no token). */
  body: Record<string, unknown>;
  /** When the write was first attempted (ms). */
  ts: number;
}

/** Sends one entry; resolves on success, throws on a network failure. */
export type OutboxSender = (entry: OutboxEntry) => Promise<void>;

const DEFAULT_MAX = 500;

/**
 * Build the room's outbox at `~/.aethereum/outbox/<hash>.jsonl`. The file name is
 * a hash of the session token, never the token itself, so the token is not
 * written to disk here (it is only re-added in memory at replay time).
 */
export function outboxForToken(sessionToken: string, home: string = homedir()): WriteOutbox {
  const hash = createHash("sha256").update(sessionToken).digest("hex").slice(0, 16);
  return new WriteOutbox(join(home, ".aethereum", "outbox", `${hash}.jsonl`));
}

export class WriteOutbox {
  private dropped = 0;

  constructor(
    private readonly file: string,
    private readonly maxEntries = DEFAULT_MAX,
  ) {}

  /** Append a write to the backlog, trimming the oldest if we exceed the cap. */
  enqueue(path: string, body: Record<string, unknown>, ts: number): void {
    const entry: OutboxEntry = { path, body, ts };
    try {
      const dir = dirname(this.file);
      if (!existsSync(dir)) mkdirSync(dir, { recursive: true });
      const current = this.readAll();
      current.push(entry);
      // Bound the backlog: drop from the FRONT (oldest) so recent intent survives.
      while (current.length > this.maxEntries) {
        current.shift();
        this.dropped++;
      }
      // If we were within the cap, a plain append avoids rewriting the whole file.
      if (current.length <= this.maxEntries && this.dropped === 0) {
        appendFileSync(this.file, JSON.stringify(entry) + "\n");
      } else {
        this.rewrite(current);
      }
    } catch {
      // The outbox is itself fail-soft: if we cannot persist, the write is lost
      // exactly as it was before the outbox existed — never break the agent.
    }
  }

  /** Number of entries currently queued (0 if the file is absent/unreadable). */
  size(): number {
    return this.readAll().length;
  }

  /** How many entries were dropped to honour the cap, this process's lifetime. */
  droppedCount(): number {
    return this.dropped;
  }

  /**
   * Replay queued writes oldest-first through `send`.
   *
   * A failed `send` is classified: `isPermanentFailure(err) === true` means the
   * server SAW and rejected the write (a 4xx/5xx — it will never succeed on
   * retry), so that entry is DROPPED and draining continues past it; otherwise
   * the failure is transient (still offline), so we stop and keep that entry plus
   * every later one, preserving order. Without a classifier every failure is
   * treated as transient (the original behaviour).
   *
   * The processed entries are always a contiguous front prefix (we only stop on a
   * transient failure), so we remove them by re-reading the file at commit time
   * and slicing that prefix off — this preserves any entries a concurrent
   * `enqueue()` appended during an `await send(...)` gap. Returns the count
   * actually delivered. Safe to call when empty (returns 0).
   */
  async drain(send: OutboxSender, isPermanentFailure?: (err: unknown) => boolean): Promise<number> {
    const entries = this.readAll();
    if (entries.length === 0) return 0;
    let flushed = 0;
    let consumed = 0; // contiguous front prefix processed (delivered or dropped-as-permanent)
    for (const entry of entries) {
      try {
        await send(entry);
        flushed++;
        consumed++;
      } catch (err) {
        if (isPermanentFailure?.(err)) {
          consumed++; // poison entry: drop it and keep draining the rest
          continue;
        }
        break; // still offline — leave this and the rest for next time
      }
    }
    if (consumed > 0) {
      // Re-read so concurrently-appended entries survive; the processed entries
      // are the front `consumed` of the (possibly grown) file.
      const current = this.readAll();
      this.rewrite(current.slice(consumed));
    }
    return flushed;
  }

  private readAll(): OutboxEntry[] {
    try {
      if (!existsSync(this.file)) return [];
      const raw = readFileSync(this.file, "utf8");
      const out: OutboxEntry[] = [];
      for (const line of raw.split("\n")) {
        const t = line.trim();
        if (!t) continue;
        try {
          const e = JSON.parse(t) as OutboxEntry;
          if (e && typeof e.path === "string" && e.body && typeof e.body === "object") out.push(e);
        } catch {
          // skip a corrupt line rather than lose the whole backlog
        }
      }
      return out;
    } catch {
      return [];
    }
  }

  private rewrite(entries: OutboxEntry[]): void {
    try {
      const dir = dirname(this.file);
      if (!existsSync(dir)) mkdirSync(dir, { recursive: true });
      writeFileSync(this.file, entries.map((e) => JSON.stringify(e)).join("\n") + (entries.length ? "\n" : ""));
    } catch {
      // fail-soft — see enqueue()
    }
  }
}
